/**
 * SelfDeclareForm — the nurse states a skill about herself, by hand. No AI, no
 * suggestion gate: she names it, she submits, it lands on her stream as a
 * declaration.
 *
 * Two inputs, because the contract's `CreateDeclarationRequest` takes a
 * catalogue `skill_id`, a free `label`, or BOTH, and naming neither is refused
 * with 422:
 *
 *  - the catalogue select names a skill the tenant catalogue defines, which is
 *    what makes the skill discoverable under the same key as everyone else's;
 *  - the label field is her own words, which is how she records something the
 *    catalogue does not yet name.
 *
 * Filling both is a real case and the form allows it: the catalogue key with
 * her own wording on top of it.
 *
 * The catalogue is a separate read with three distinct answers, and the form
 * says which one it got. A deployment that has declared no catalogue answers
 * 503 SKILLS_NOT_CONFIGURED, which is a STATE and not a failure — telling a
 * nurse her catalogue "could not be loaded" there sends her to report a fault
 * that does not exist. A genuine failure says that instead. Neither is ever
 * rendered as an empty catalogue, and neither hides the label field: the two
 * inputs are independent, and the contract's `anyOf` means a free label alone
 * is a complete declaration.
 *
 * A declaration is private on creation. Publishing is a separate decision on
 * the skill row, so this form does not ask for consent it does not need.
 *
 * DESIGN.md: pill CTA in the single Action Blue, scale-0.95 press, 44px tap
 * target, flat hairline input.
 */

import { useState, type FormEvent } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@frontend/components/ui/input'
import { Button } from '@frontend/components/ui/button'
import { ApiError } from '@frontend/api/client'
import type { DeclareSkillRequest } from '@frontend/api/endpoints/skills'
import { useSkillCatalogue } from '@frontend/hooks/useSkills'

interface SelfDeclareFormProps {
  onDeclare: (request: DeclareSkillRequest) => Promise<unknown>
  busy: boolean
}

/** The value the select carries when the nurse has chosen no catalogue skill. */
const NO_CATALOGUE_SKILL = ''

export function SelfDeclareForm({ onDeclare, busy }: SelfDeclareFormProps) {
  const { t, i18n } = useTranslation()
  const [label, setLabel] = useState('')
  const [skillId, setSkillId] = useState(NO_CATALOGUE_SKILL)
  const { data: catalogue, isLoading, isError, error } = useSkillCatalogue()

  // Branch on the CODE, not the status: a 503 from a cold-starting service and
  // a 503 saying no catalogue is declared are the same number and different
  // answers, and only one of them is worth telling her about as a state.
  const notConfigured = error instanceof ApiError && error.code === 'SKILLS_NOT_CONFIGURED'

  const trimmedLabel = label.trim()
  const canSubmit = !busy && (trimmedLabel.length > 0 || skillId !== NO_CATALOGUE_SKILL)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const request: DeclareSkillRequest = {
      ...(skillId === NO_CATALOGUE_SKILL ? {} : { skill_id: skillId }),
      ...(trimmedLabel === '' ? {} : { label: trimmedLabel }),
    }
    // Clear ONLY after the write lands. If it fails (the hook toasts it), she
    // keeps what she typed instead of silently losing it.
    try {
      await onDeclare(request)
      setLabel('')
      setSkillId(NO_CATALOGUE_SKILL)
    } catch {
      /* useOwnSkillMutations.onError surfaced the failure */
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('skills.form.labelPlaceholder')}
          aria-label={t('skills.form.labelAriaLabel')}
          className="min-h-11 flex-1 rounded-full border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 text-[17px]"
          enterKeyHint="done"
        />
        <Button
          type="submit"
          size="pill"
          disabled={!canSubmit}
          aria-label={t('skills.form.addButton')}
          className="min-h-11 shrink-0"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">{t('skills.form.addButton')}</span>
        </Button>
      </div>

      {isError ? (
        // A state reads as quiet prose; a fault reads as a warning. Painting
        // "this deployment has no catalogue" in the warn tone would make an
        // ordinary configuration look broken on every profile.
        <p
          className={
            notConfigured
              ? 'text-[13px] text-[var(--color-surface-mute)]'
              : 'text-[13px] text-[var(--color-tone-warn)]'
          }
        >
          {notConfigured ? t('skills.form.catalogueNotConfigured') : t('skills.form.catalogueFailed')}
        </p>
      ) : (
        <select
          value={skillId}
          onChange={(e) => setSkillId(e.target.value)}
          disabled={isLoading || !catalogue}
          aria-label={t('skills.form.catalogueAriaLabel')}
          className="min-h-11 w-full rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 text-[15px] text-[var(--color-surface-ink)]"
        >
          <option value={NO_CATALOGUE_SKILL}>
            {isLoading ? t('skills.form.catalogueLoading') : t('skills.form.catalogueNone')}
          </option>
          {catalogue?.items.map((skill) => (
            <option key={skill.id} value={skill.id}>
              {/* The contract guarantees every supported locale is present, so
                  the active locale's title is the name. When a deployment's
                  catalogue disagrees with its locale list the id shows through,
                  which is the real key and not an invented placeholder. */}
              {skill.title[i18n.language] ?? skill.id}
            </option>
          ))}
        </select>
      )}
    </form>
  )
}

export default SelfDeclareForm
